import { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Chip,
    Grid,
    Popover,
    Skeleton,
    Stack,
    Typography,
} from '@mui/material';
import { HelpOutlineRounded as HelpIcon } from '@mui/icons-material';

import { useSubscriptionPlans } from '@/hooks/queries/subscription/useSubscriptionPlans';
import { usePaymentMethods } from '@/hooks/queries/subscription/usePaymentMethods';
import type { BillingCycle, SubscriptionDto } from '@/types/subscription';

import PlanCard from './PlanCard';
import { ACCENT } from '../_shared';

interface PlanGridSectionProps {
    subscription: SubscriptionDto | null | undefined;
}

const PlanGridSection = ({ subscription }: PlanGridSectionProps) => {
    const { data: plans, isLoading } = useSubscriptionPlans();
    const { data: methods } = usePaymentMethods();

    // 현재 구독자라면 자기 주기로 시작, 아니면 monthly
    const [cycle, setCycle] = useState<BillingCycle>(
        (subscription?.billingCycle as BillingCycle | undefined) ?? 'monthly',
    );

    // 비용 안내 팝오버 anchor
    const [costGuideAnchor, setCostGuideAnchor] = useState<HTMLElement | null>(null);

    const defaultMethod = methods?.find((m) => m.isDefault === 't') ?? methods?.[0];
    const hasPaymentMethod = !!methods && methods.length > 0;
    const hasActiveSubscription = subscription?.status === 'active';

    // 모든 plan_price 중 yearly 의 최대 할인율(%) — 토글 옆 Chip 라벨용
    const maxYearlyDiscount = (plans ?? [])
        .flatMap((p) => p.prices ?? [])
        .filter((p) => p.billingCycle === 'yearly' && p.originalAmount && p.originalAmount > p.amount)
        .reduce((max, p) => {
            const ratio = Math.round((1 - p.amount / (p.originalAmount as number)) * 100);
            return ratio > max ? ratio : max;
        }, 0);
    const yearlyChipLabel = maxYearlyDiscount > 0 ? `${maxYearlyDiscount}% 할인` : '';

    const visiblePlans = (plans ?? []).slice().sort((a, b) => a.tier - b.tier);
    // 보이는 카드 개수에 따라 md 사이즈 계산 — 한 줄에 균등 배치
    const mdSize = visiblePlans.length <= 1
        ? 6
        : visiblePlans.length === 2
        ? 5
        : visiblePlans.length === 3
        ? 4
        : 3;

    return (
        <Box>
            {/* 헤더 — 제목/서브타이틀/토글 모두 중앙 정렬 */}
            <Stack alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                <Stack alignItems="center" spacing={0.5}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                        플랜 선택
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        언제든 변경하거나 해지할 수 있어요
                    </Typography>
                </Stack>

                {/* 세그먼트 컨트롤 — pill 컨테이너 + ACCENT fill 인디케이터 */}
                <Box
                    role="tablist"
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        bgcolor: 'rgba(0,0,0,0.04)',
                        borderRadius: 999,
                        p: 0.5,
                        gap: 0.25,
                        boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.04)',
                    }}
                >
                    {[
                        { value: 'monthly' as BillingCycle, label: '월 결제' },
                        { value: 'yearly' as BillingCycle, label: '연 결제' },
                    ].map((opt) => {
                        const active = cycle === opt.value;
                        return (
                            <Box
                                key={opt.value}
                                role="tab"
                                aria-selected={active}
                                onClick={() => setCycle(opt.value)}
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    px: 2.25,
                                    py: 0.9,
                                    borderRadius: 999,
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                    bgcolor: active ? ACCENT : 'transparent',
                                    color: active ? '#fff' : 'text.secondary',
                                    boxShadow: active
                                        ? '0 4px 16px rgba(251,109,72,0.42), inset 0 0 0 1px rgba(251,109,72,0.5)'
                                        : 'none',
                                    transition:
                                        'background-color 240ms cubic-bezier(.2,.8,.2,1), color 200ms ease, box-shadow 240ms ease, transform 160ms ease',
                                    '&:hover': active
                                        ? undefined
                                        : {
                                              bgcolor: 'rgba(251,109,72,0.08)',
                                              color: ACCENT,
                                          },
                                    '&:active': { transform: 'scale(0.97)' },
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: 13.5,
                                        fontWeight: active ? 800 : 600,
                                        color: 'inherit',
                                        letterSpacing: '-0.01em',
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {opt.label}
                                </Typography>
                                {opt.value === 'yearly' && yearlyChipLabel && (
                                    <Chip
                                        size="small"
                                        label={yearlyChipLabel}
                                        sx={{
                                            height: 18,
                                            fontSize: 10,
                                            fontWeight: 700,
                                            bgcolor: active ? 'rgba(255,255,255,0.22)' : ACCENT,
                                            color: '#fff',
                                            border: active
                                                ? '1px solid rgba(255,255,255,0.4)'
                                                : 'none',
                                            boxShadow: active
                                                ? 'none'
                                                : '0 0 10px rgba(251,109,72,0.35)',
                                            '& .MuiChip-label': { px: 0.75 },
                                        }}
                                    />
                                )}
                            </Box>
                        );
                    })}
                </Box>

                {/* 후킹 멘트 — 플랜 변경 비용 안내 */}
                <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    onClick={(e) => setCostGuideAnchor(e.currentTarget)}
                    sx={{
                        cursor: 'pointer',
                        py: 0.5,
                        px: 1,
                        borderRadius: 999,
                        color: costGuideAnchor ? ACCENT : 'text.secondary',
                        transition: 'color 180ms ease, background-color 180ms ease',
                        '&:hover': {
                            color: ACCENT,
                            bgcolor: 'rgba(251,109,72,0.06)',
                        },
                    }}
                >
                    <HelpIcon sx={{ fontSize: 14, color: 'inherit' }} />
                    <Typography
                        variant="caption"
                        sx={{
                            fontWeight: 600,
                            color: 'inherit',
                            letterSpacing: '-0.01em',
                            textDecoration: 'underline',
                            textDecorationStyle: 'dotted',
                            textUnderlineOffset: 3,
                        }}
                    >
                        플랜 변경 비용은 어떻게 계산되나요?
                    </Typography>
                </Stack>
            </Stack>

            {isLoading ? (
                <Grid container spacing={2} justifyContent="center">
                    {[0, 1, 2].map((i) => (
                        <Grid key={i} item xs={12} sm={6} md={mdSize}>
                            <Card variant="outlined" sx={{ borderRadius: 3, height: 320 }}>
                                <CardContent>
                                    <Skeleton variant="text" width="50%" height={28} />
                                    <Skeleton variant="text" width="40%" height={48} sx={{ my: 1 }} />
                                    <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 1 }} />
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            ) : (
                <Grid container spacing={2} justifyContent="center">
                    {visiblePlans.map((plan) => (
                        <Grid key={plan.id} item xs={12} sm={6} md={mdSize}>
                            <PlanCard
                                plan={plan}
                                cycle={cycle}
                                currentPlanCode={subscription?.plan?.code}
                                currentBillingCycle={
                                    subscription?.billingCycle as BillingCycle | undefined
                                }
                                hasActiveSubscription={hasActiveSubscription}
                                hasPaymentMethod={hasPaymentMethod}
                                defaultPaymentMethod={defaultMethod}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* 플랜 변경 비용 안내 팝오버 */}
            <Popover
                open={!!costGuideAnchor}
                anchorEl={costGuideAnchor}
                onClose={() => setCostGuideAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                PaperProps={{
                    sx: {
                        mt: 1,
                        p: 2.25,
                        maxWidth: 360,
                        borderRadius: 1.5,
                        boxShadow: '0 16px 40px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)',
                    },
                }}
            >
                <Stack spacing={1.5}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        플랜 변경 비용
                    </Typography>

                    {/* 업그레이드 */}
                    <Box>
                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
                            <Box
                                sx={{
                                    width: 4,
                                    height: 4,
                                    borderRadius: '50%',
                                    bgcolor: ACCENT,
                                }}
                            />
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                업그레이드 (예: 퍼스널 → 비즈니스)
                            </Typography>
                        </Stack>
                        <Stack spacing={0.4} sx={{ pl: 1.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                · 이용 중인 결제 주기의 <b>잔여 일수에 대한 차액만</b> 즉시 결제돼요.
                            </Typography>
                            <Box
                                component="code"
                                sx={{
                                    display: 'inline-block',
                                    px: 0.75,
                                    py: 0.4,
                                    bgcolor: 'rgba(0,0,0,0.05)',
                                    borderRadius: 0.75,
                                    fontFamily: '"SF Mono", "JetBrains Mono", ui-monospace, monospace',
                                    fontSize: 11,
                                    lineHeight: 1.5,
                                    color: 'text.primary',
                                }}
                            >
                                (새 플랜가 − 기존 플랜가) × 잔여일 ÷ 총일
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                · 이용 기간과 다음 결제일은 그대로 유지돼요.
                            </Typography>
                        </Stack>
                    </Box>

                    <Box sx={{ height: 1, bgcolor: 'divider' }} />

                    {/* 다운그레이드 */}
                    <Box>
                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
                            <Box
                                sx={{
                                    width: 4,
                                    height: 4,
                                    borderRadius: '50%',
                                    bgcolor: 'text.secondary',
                                }}
                            />
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                다운그레이드 (예: 비즈니스 → 퍼스널)
                            </Typography>
                        </Stack>
                        <Stack spacing={0.4} sx={{ pl: 1.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                · 지금은 결제가 발생하지 않아요.
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                · 다음 결제일에 새 플랜으로 <b>자동 전환</b>되고 새 가격으로 결제돼요.
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                · 예약은 언제든 "변경 예약 취소"로 되돌릴 수 있어요.
                            </Typography>
                        </Stack>
                    </Box>

                    <Box sx={{ height: 1, bgcolor: 'divider' }} />

                    {/* Free 다운 */}
                    <Box>
                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
                            <Box
                                sx={{
                                    width: 4,
                                    height: 4,
                                    borderRadius: '50%',
                                    bgcolor: 'text.secondary',
                                }}
                            />
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                Free 변경 (예: 퍼스널 → Free)
                            </Typography>
                        </Stack>
                        <Stack spacing={0.4} sx={{ pl: 1.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                · 결제는 발생하지 않아요.
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                · 이용 기간 종료일에 <b>자동 해지</b>되고 환불은 발생하지 않아요.
                            </Typography>
                        </Stack>
                    </Box>
                </Stack>
            </Popover>
        </Box>
    );
};

export default PlanGridSection;
